import { InvoiceColumn } from '@/context/services/invoice/type';
import { useEmptyState } from '@/features/invoices/components/List/hooks';
import { Uploader } from '@/features/invoices/components/List/Table/Uploader/Uploader';
import { usePaymentRequestSupportAgentFlag } from '@/hooks/feature_flag/usePaymentRequestSupportAgentFlag';
import { useIsPaymentRequestSupportAgentEnabled } from '@/hooks/useIsPaymentRequestSupportAgentEnabled';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  ModernPaginationProps,
  Table,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo, useState } from 'react';
import { useColumns } from './Columns';
import styles from './Table.module.scss';

const cx = classnames.bind(styles);

export type InternalTableProps = Omit<
  TableProps<InvoiceColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

export type Props = {
  pagination: ModernPaginationProps;
  table: InternalTableProps;
  isSearchEmpty?: boolean;
  callRefetch: VoidFunction;
};

export const InvoiceTable: FC<Props> = memo(
  ({ pagination, table, isSearchEmpty = false, callRefetch }) => {
    const columns = useColumns();
    const { emptyType, emptyMessage } = useEmptyState(isSearchEmpty);
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const isPaymentRequestSupportAgentEnabled =
      usePaymentRequestSupportAgentFlag();

    const { enabled: isAgentSettingEnabled } =
      useIsPaymentRequestSupportAgentEnabled(
        Boolean(open && isPaymentRequestSupportAgentEnabled)
      );
    const showAiSuggest = Boolean(
      isPaymentRequestSupportAgentEnabled && isAgentSettingEnabled
    );

    // Total minimum width of all columns for scroll calculation
    const scroll = useMemo(() => ({ x: 840 }), []);

    return (
      <div
        className={cx(styles['container'], 'invoice-list-ui-enhanced')}
        data-testid='invoice-table'
      >
        <Table
          emptyType={emptyType}
          {...table}
          legacy={false}
          key={pagination.currentPage}
          testId='invoice-table'
          emptyText={emptyMessage}
          columns={columns}
          pagination={pagination}
          tableLayout='fixed'
          scroll={scroll}
          tableAction={
            <ButtonV2 onClick={() => setOpen(true)}>{t('upload')}</ButtonV2>
          }
        />
        <Uploader
          open={open}
          setOpen={setOpen}
          callRefetch={callRefetch}
          showAiSuggest={showAiSuggest}
          enableAiSuggest={isPaymentRequestSupportAgentEnabled}
        />
      </div>
    );
  }
);
InvoiceTable.displayName = 'InvoiceTable';

export const LoadingInvoiceTable: FC = memo(() => {
  const isOnline = useNetworkStatus();
  const pagination = useMemo(
    () => ({
      currentPage: 1,
      maxPage: 1,
      perPage: 1,
      maxRecord: 0,
    }),
    []
  );
  const tableProps = useMemo(
    () => ({ data: [], loading: isOnline }),
    [isOnline]
  );
  return (
    <InvoiceTable
      table={tableProps}
      pagination={pagination}
      isSearchEmpty={false}
      callRefetch={() => {}}
    />
  );
});
LoadingInvoiceTable.displayName = 'LoadingInvoiceTable';

type ErrorInvoiceTableProps = {
  error: Error | null;
};

export const ErrorInvoiceTable: FC<ErrorInvoiceTableProps> = memo(() => {
  const pagination = useMemo(
    () => ({
      currentPage: 1,
      maxPage: 1,
      perPage: 1,
      maxRecord: 0,
    }),
    []
  );
  const tableProps = useMemo(
    () => ({ data: [], loading: false, emptyType: 'error' }),
    []
  );
  return (
    <InvoiceTable
      table={tableProps}
      pagination={pagination}
      isSearchEmpty={false}
      callRefetch={() => {}}
    />
  );
});
ErrorInvoiceTable.displayName = 'ErrorInvoiceTable';
